import React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import { useMyGames, useMyProfile } from "@seabrookstudios/pitch2table";
import { useNavigate } from "react-router-dom";
import { Path } from "../Path";
import { useTheme } from "@mui/material/styles";

/**
 * Check if personal profile section is complete
 * @param {import("@seabrookstudios/pitch2table-core").AdminOnlyPublicContact} profile
 */
const isPersonalComplete = (profile) => {
  if (!profile) return false;
  if (!profile.firstName || profile.firstName.trim() === "") return false;
  if (!profile.emailVerified) return false;
  return true;
};

/**
 * Check if location profile section is complete
 * @param {import("@seabrookstudios/pitch2table-core").AdminOnlyPublicContact} profile
 */
const isLocationComplete = (profile) => {
  if (!profile) return false;
  if (!profile.location) return false;
  if (!profile.location.timezone || profile.location.timezone.trim() === "") return false;
  return true;
};

/**
 * Check if marketing consent profile section is complete
 * @param {import("@seabrookstudios/pitch2table-core").AdminOnlyPublicContact} profile
 */
const isMarketingConsentComplete = (profile) => {
  if (!profile) return false;
  return profile.notificationSettings?.marketingConsent !== undefined;
};

/**
 * Check if entire profile is complete
 * @param {import("@seabrookstudios/pitch2table-core").AdminOnlyPublicContact} profile
 */
export const isProfileComplete = (profile) => {
  if (!profile) return false;

  return isPersonalComplete(profile) && isLocationComplete(profile) && isMarketingConsentComplete(profile);
};

/**
 * Profile completion section component
 * @param {object} props
 * @param {string} props.title
 * @param {string[]} props.requirements
 * @param {string} props.reason
 * @param {boolean} props.isComplete
 * @param {() => void} props.onClick
 */
const ProfileSection = ({ title, requirements, reason, isComplete, onClick }) => {
  return (
    <ListItemButton onClick={onClick}>
      <ListItemIcon>
        {isComplete ? <CheckCircle color="success" /> : <RadioButtonUnchecked color="primary" />}
      </ListItemIcon>
      <ListItemText
        primary={title}
        secondary={
          <Typography variant="body2" color="text.primary">
            {requirements.join(", ")} - {reason}
          </Typography>
        }
      />
    </ListItemButton>
  );
};

export const ProfileCompletionCard = () => {
  const { data: profile, isLoading } = useMyProfile();
  const { data: games = [] } = useMyGames();
  const theme = useTheme();
  const navigate = useNavigate();

  if (isLoading) {
    return null;
  }

  if (!profile) {
    return null;
  }

  if (isProfileComplete(profile)) {
    return null;
  }

  if (games.length > 0) {
    return null;
  }

  const personalComplete = isPersonalComplete(profile);
  const locationComplete = isLocationComplete(profile);
  const marketingConsentComplete = isMarketingConsentComplete(profile);

  const handlePersonalClick = () => {
    navigate(Path.settings().category("about-you"));
  };

  const handleLocationClick = () => {
    navigate(Path.settings().category("your-location"));
  };

  const handleMarketingConsentClick = () => {
    navigate(Path.settings().category("notifications"));
  };

  return (
    <Card variant="outlined" style={{ borderColor: theme.palette.primary.main }}>
      <CardHeader title="Complete your profile" />
      <CardContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Some pitch2table features need information from your profile. Complete the essentials to unlock these
          features.
        </Typography>
        <List>
          <ProfileSection
            title="Personal"
            requirements={["Name", "Email verification"]}
            reason="Used when you contact support."
            isComplete={personalComplete}
            onClick={handlePersonalClick}
          />
          <ProfileSection
            title="Location"
            requirements={["Timezone", "Currency"]}
            reason="Shown on your profile and lets us show royalty amounts in your local currency."
            isComplete={locationComplete}
            onClick={handleLocationClick}
          />
          <ProfileSection
            title="Marketing Consent"
            requirements={["Marketing preferences"]}
            reason="So we can share updates about new features.."
            isComplete={marketingConsentComplete}
            onClick={handleMarketingConsentClick}
          />
        </List>
      </CardContent>
    </Card>
  );
};
